import { MapViewerService, Viewer3dService } from '@3ddv/ngx-dvm-internal';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  EffectRef,
  ElementRef,
  inject,
  OnDestroy,
  Signal,
  ViewChild,
} from '@angular/core';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { Disclaimer } from '@models/types/components/Disclaimer.type';
import { DvmService } from '@services/dvm.service';
import { UiService } from '@services/ui.service';
import anime from 'animejs';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-disclaimer',
  standalone: true,
  imports: [],
  templateUrl: './disclaimer.component.html',
  styleUrl: './disclaimer.component.css',
})
export class DisclaimerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('disclaimerContainer')
  private readonly disclaimerContainer!: ElementRef<HTMLDivElement>;

  //SERVICES
  private readonly dvmService: DvmService = inject(DvmService);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  //CONFIG
  private readonly uiconfig: Signal<UiConfiguration> =
    inject(UiService).uiConfig;
  private readonly disclaimer: Signal<Disclaimer> = computed(
    (): Disclaimer => this.uiconfig()['Disclaimer']
  );

  //STATE
  private readonly viewer: MapViewerService = this.dvmService.viewer;
  private readonly viewer3d: Viewer3dService = this.dvmService.viewer3d;

  private readonly view: Signal<AppViews | null> = this.dvmService.view;
  protected showElement: boolean = false;
  protected disclaimerText: string | null = null;
  private lastDisclaimer: Disclaimer | null = null;

  private readonly Suscriptions: Subscription[] = [];
  private previousView: AppViews | null = null;

  private onViewChange: EffectRef = effect(() => {
    const view = this.view();
    const disclaimer = this.disclaimer();

    if (!disclaimer || !view) return;

    // Check if navigating away from pano view to any other view (as this would not trigger load_success)
    const panoToOtherView =
      this.previousView === AppViews.Pano && view !== this.previousView;

    // Detect disclaimer changes - effect re-runs any time this.disclaimer() is updated
    const disclaimerChanged = this.lastDisclaimer !== disclaimer;

    //run handler if navigating away from pano OR disclaimer signal has changed
    if (panoToOtherView || disclaimerChanged) {
      this.handleDisclaimer(view);
    }

    this.lastDisclaimer = disclaimer;
  });

  ngAfterViewInit(): void {
    this.Suscriptions.push(
      this.viewer
        .getObservable('load_success')
        .subscribe(() => setTimeout(() => this.handleDisclaimer(this.view()))),
      this.viewer3d
        .getObservable('load_success')
        .subscribe(() => setTimeout(() => this.handleDisclaimer(this.view())))
    );
  }

  ngOnDestroy(): void {
    this.Suscriptions.forEach((sub) => sub.unsubscribe());
  }

  private handleDisclaimer(view: AppViews | null): void {
    const disclaimer = this.disclaimer();

    // If disclaimer is globally disabled, hide disclaimer and exit
    if (!disclaimer.enabled) {
      this.toggleComponentVisibility(false);
      return;
    }

    const { allNodes } = disclaimer;
    const isSeatmapView = view === AppViews.Seatmap;
    const isPanoView = view === AppViews.Pano;

    // Handle ALL NODES mode first
    const isAllNodesMode =
      allNodes.enabled &&
      (allNodes.allSeatmaps || allNodes.all3dViews) &&
      (isSeatmapView || isPanoView);

    if (isAllNodesMode) {
      this.handleAllNodesDisclaimer(view);
      this.saveState(view);
      return;
    }

    // Get current map and pano data, applying fallback for pano IDs (check seat, row + section)
    const currentMapKey = this.dvmService.currentMap!;
    const panoViewId = this.dvmService.currentView3d!;

    const mapData = this.disclaimer().data[currentMapKey];
    const panoData = panoViewId
      ? this.getDisclaimerDataWithFallback(panoViewId)
      : null;

    // Determine which dataset applies
    let selectedData = null;
    if (isPanoView && panoData?.pano) {
      selectedData = panoData;
    } else if (isSeatmapView && mapData?.seatmap) {
      selectedData = mapData;
    }

    // Show or hide the component
    if (selectedData?.text) {
      this.toggleComponentVisibility(true);
      this.disclaimerText = this.formatText(selectedData.text);
    } else {
      this.toggleComponentVisibility(false);
      this.disclaimerText = null;
    }

    // Save previous view for when we go from pano back to seatmap
    this.saveState(view);
  }

  private handleAllNodesDisclaimer(view: AppViews | null): void {
    const showDisclaimer =
      (view === AppViews.Seatmap && this.disclaimer().allNodes.allSeatmaps) ||
      (view === AppViews.Pano && this.disclaimer().allNodes.all3dViews);

    if (!showDisclaimer) {
      this.toggleComponentVisibility(false);
      this.disclaimerText = null;
      return;
    }

    this.toggleComponentVisibility(true);
    this.disclaimerText = this.formatText(this.disclaimer().allNodes.text);
  }

  private saveState(view: AppViews | null): void {
    this.previousView = view;
    this.cdr.detectChanges();
  }

  private formatText(text: string): string {
    return text.replace(/\\n|\n/g, '<br>');
  }

  private getDisclaimerDataWithFallback(id: string) {
    const data = this.disclaimer().data;

    const parts = id.split('-');
    while (parts.length > 0) {
      const key = parts.join('-');
      if (data[key]) {
        return data[key];
      }
      parts.pop(); // remove last segment of ID to check seat, row, and then section in pano
    }
    return null;
  }

  close() {
    this.toggleComponentVisibility(false);
  }

  private toggleComponentVisibility(show: boolean): void {
    if (!this.showElement && show) this.showElement = true;

    anime({
      targets: this.disclaimerContainer.nativeElement,
      opacity: show ? 1 : 0,
      easing: 'easeInOutQuad',
      duration: 300,
      complete: (): void => {
        if (!show) this.showElement = false;
      },
    });
  }
}
